/**
 * "Schedule All…" — één gedeelde start, einddatum en interval voor alle
 * geselecteerde modellen, via POST /api/admin/posting/bulk-start.
 *
 * FanCentro zet geplande posts pas live bij minstens 24 uur aanloop. De
 * dialoog vult daarom standaard nu + 25 uur in (afgerond op hele 5 minuten)
 * met een einde 7 dagen later, en waarschuwt zodra de start binnen 24 uur
 * valt. Zonder bevestigingsvinkje blokkeert Toepassen; met vinkje gaat
 * `allow_short_lead` mee, en dan laat de backend het bewust door (en logt
 * `bulk_start_short_lead`). Zonder die vlag weigert de backend met 422.
 */
import { useMemo, useState } from 'react'
import { api } from '../lib/api'
import type { Persona } from '../pages/Models'

const MIN_LEAD_MS = 24 * 3600 * 1000

function localValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

function defaults(): { start: string; end: string } {
  const s = new Date(Date.now() + 25 * 3600 * 1000)
  s.setSeconds(0, 0)
  s.setMinutes(Math.round(s.getMinutes() / 5) * 5)
  const e = new Date(s.getTime() + 7 * 24 * 3600 * 1000)
  return { start: localValue(s), end: localValue(e) }
}

export function ScheduleAllDialog({
  personas,
  platformVast,
  onClose,
  onDone,
}: {
  personas: Persona[]
  /** Voorgekozen platform als de dialoog vanaf een Start All-knop komt; de
   *  keuze staat dan vast, want die knop zégt al welk platform je bedoelt. */
  platformVast?: 'all' | 'fc' | 'f2f'
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const d0 = useMemo(defaults, [])
  const [platform, setPlatform] = useState<'all' | 'fc' | 'f2f'>(platformVast ?? 'all')
  const [start, setStart] = useState(d0.start)
  const [end, setEnd] = useState(d0.end)
  const [interval, setIntervalMin] = useState('30')
  const [repeat, setRepeat] = useState(true)
  const [ack, setAck] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const shortLead = useMemo(() => {
    if (!start) return false
    return new Date(start).getTime() - Date.now() < MIN_LEAD_MS
  }, [start])

  const ids = useMemo(() => {
    return personas
      .filter((p) => !p.posting_hidden)
      .filter((p) =>
        platform === 'all' ? true
        : platform === 'fc' ? !!p.fancentro_model_id && !p.f2f_model_id
        : !!p.f2f_model_id)
      .map((p) => p.id)
  }, [personas, platform])

  async function apply() {
    setErr('')
    const n = parseInt(interval, 10)
    if (!start || !end || !(n > 0)) { setErr('Fill in start, end and interval.'); return }
    if (new Date(end) <= new Date(start)) { setErr('End must be after start.'); return }
    if (shortLead && !ack) {
      setErr('Tick the box if you want to apply this short lead time anyway.')
      return
    }
    if (!ids.length) { setErr('No models in this selection.'); return }
    setBusy(true)
    try {
      const res = await api.post<{ applied?: unknown[]; skipped?: unknown[] }>(
        '/api/admin/posting/bulk-start',
        {
          model_persona_ids: ids,
          start_from: new Date(start).toISOString(),
          auto_stop_at: new Date(end).toISOString(),
          interval_minutes: n,
          repeat,
          platform,
          allow_short_lead: shortLead && ack,
        },
      )
      const a = res.applied?.length ?? 0
      const s = res.skipped?.length ?? 0
      onDone(`Schedule All (${platform}): ${a} set${s ? `, ${s} skipped` : ''}`)
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      // Niet sluiten bij een klik op de achtergrond; zie Modal.tsx (27-09). Sluiten via Cancel.
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 99998,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="card" style={{ width: 380, maxWidth: 'calc(92vw / var(--dz, 1))' }}>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
          Schedule All — all models at once
        </div>
        <div className="mut2" style={{ fontSize: 12 }}>
          One shared start and end date for {ids.length} model(s).
        </div>

        <div className="field" style={{ marginTop: 10 }}>
          <label>Platform</label>
          <select className="inp" value={platform}
                  onChange={(e) => setPlatform(e.target.value as 'all' | 'fc' | 'f2f')}>
            <option value="all">All models (FC + F2F)</option>
            <option value="fc">FC only</option>
            <option value="f2f">F2F only</option>
          </select>
        </div>
        <div className="field">
          <label>Start (date/time)</label>
          <input className="inp" type="datetime-local" value={start}
                 onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="field">
          <label>End (date/time)</label>
          <input className="inp" type="datetime-local" value={end}
                 onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="field">
          <label>Interval (minutes)</label>
          <input className="inp" type="number" min={1} value={interval}
                 onChange={(e) => setIntervalMin(e.target.value)} />
        </div>
        <label className="chkrow">
          <span className={repeat ? 'chk on' : 'chk'} onClick={() => setRepeat((v) => !v)}>
            {repeat ? '✓' : ''}
          </span>
          Repeat from #1 until the end date
        </label>

        {shortLead ? (
          <div style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 6,
            border: '1px solid #b71c1c', background: 'rgba(183,28,28,.18)',
          }}>
            <div style={{ color: '#ff8a80', fontSize: 12, lineHeight: 1.4 }}>
              FanCentro needs at least 24 hours — this start will NOT go live
              (only useful for a dry run or test)
            </div>
            <label className="chkrow" style={{ color: '#ffcdd2', fontSize: 12 }}>
              <span className={ack ? 'chk on' : 'chk'} onClick={() => setAck((v) => !v)}>
                {ack ? '✓' : ''}
              </span>
              I know, apply anyway
            </label>
          </div>
        ) : null}

        <div style={{ color: '#ff8a80', fontSize: 12, minHeight: 16, marginTop: 8 }}>{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" type="button" disabled={busy} onClick={apply}>
            {busy ? 'Working…' : 'Apply to all'}
          </button>
        </div>
      </div>
    </div>
  )
}
